import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Globe, Users, Mic } from 'lucide-react';
import { getProjects, createProject, updateProject, getUsers } from '../services/api';
import MetadataFormBuilder from '../components/MetadataFormBuilder';
import { MetadataForm, defaultMetadataFields } from '../types/metadataForm';

interface Project {
  projectId: string;
  name: string;
  description: string;
  status: string;
  targetLanguages: string[];
  targetLocations: string[];
  targetSessionCount?: number;
  completedSessionCount: number;
  surveyProject?: boolean;
  voiceOnly?: boolean;
  metadataForm?: MetadataForm;
  assignedSupervisorIds?: string[];
  createdAt: string;
}

interface SupervisorOption {
  userId: string;
  displayName?: string;
  email?: string;
}

interface ProjectFormState {
  name: string;
  description: string;
  targetLanguages: string;
  targetLocations: string;
  targetSessionCount: string;
  instructions: string;
  surveyProject: boolean;
  voiceOnly: boolean;
  metadataForm: MetadataForm;
  status: string;
  assignedSupervisorIds: string[];
}

const BLANK_FORM: ProjectFormState = {
  name: '',
  description: '',
  targetLanguages: '',
  targetLocations: '',
  targetSessionCount: '',
  instructions: '',
  surveyProject: false,
  voiceOnly: false,
  metadataForm: { enabled: false, fields: [] },
  status: 'active',
  assignedSupervisorIds: [],
};

export default function ProjectsPage() {
  const [showModal, setShowModal] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [form, setForm] = useState<ProjectFormState>(BLANK_FORM);
  const [supervisorFilter, setSupervisorFilter] = useState('');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['projects-all'],
    queryFn: () => getProjects({ status: 'all' }),
  });

  const { data: supervisors = [] } = useQuery<SupervisorOption[]>({
    queryKey: ['supervisors-for-projects'],
    queryFn: () =>
      getUsers({ role: 'supervisor', approvalStatus: 'approved', limit: '5000' }).then(
        (d: any) => d.users ?? d ?? [],
      ),
  });

  const projects: Project[] = data?.projects ?? [];

  const createMutation = useMutation({
    mutationFn: createProject,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      setShowModal(false);
      setForm(BLANK_FORM);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: unknown }) =>
      updateProject(projectId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      setShowModal(false);
      setEditProject(null);
      setForm(BLANK_FORM);
    },
  });

  const openCreate = () => {
    setEditProject(null);
    setForm(BLANK_FORM);
    setSupervisorFilter('');
    setShowModal(true);
  };

  const openEdit = (p: Project) => {
    setEditProject(p);
    setForm({
      name: p.name,
      description: p.description,
      targetLanguages: p.targetLanguages.join(', '),
      targetLocations: p.targetLocations.join(', '),
      targetSessionCount: p.targetSessionCount?.toString() ?? '',
      instructions: '',
      surveyProject: p.surveyProject ?? false,
      voiceOnly: p.voiceOnly ?? false,
      metadataForm: p.metadataForm ?? { enabled: false, fields: [] },
      status: p.status ?? 'active',
      assignedSupervisorIds: p.assignedSupervisorIds ?? [],
    });
    setSupervisorFilter('');
    setShowModal(true);
  };

  const toggleSupervisor = (id: string) => {
    setForm((f) => ({
      ...f,
      assignedSupervisorIds: f.assignedSupervisorIds.includes(id)
        ? f.assignedSupervisorIds.filter((x) => x !== id)
        : [...f.assignedSupervisorIds, id],
    }));
  };

  const handleSubmit = () => {
    const payload = {
      name: form.name,
      description: form.description,
      targetLanguages: form.targetLanguages.split(',').map((s) => s.trim()).filter(Boolean),
      targetLocations: form.targetLocations.split(',').map((s) => s.trim()).filter(Boolean),
      targetSessionCount: form.targetSessionCount ? parseInt(form.targetSessionCount) : undefined,
      instructions: form.instructions || undefined,
      surveyProject: form.surveyProject,
      // voiceOnly + the metadata form only apply to survey projects
      voiceOnly: form.surveyProject ? form.voiceOnly : false,
      metadataForm: form.surveyProject ? form.metadataForm : { enabled: false, fields: [] },
      assignedSupervisorIds: form.assignedSupervisorIds,
      ...(editProject ? { status: form.status } : {}),
    };

    if (editProject) {
      updateMutation.mutate({ projectId: editProject.projectId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="text-gray-500 text-sm mt-1">Manage data collection projects</p>
        </div>
        <button className="btn-primary" onClick={openCreate}>
          <Plus size={16} />
          New Project
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="card text-center py-12">
          <Globe size={40} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No projects yet. Create your first project.</p>
          <button className="btn-primary mt-4 mx-auto" onClick={openCreate}>
            <Plus size={16} /> Create Project
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map((project) => {
            const pct = project.targetSessionCount
              ? Math.min(100, Math.round((project.completedSessionCount / project.targetSessionCount) * 100))
              : null;

            return (
              <div key={project.projectId} className="card flex flex-col gap-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{project.name}</h3>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{project.description}</p>
                  </div>
                  <span
                    className={`badge flex-shrink-0 ${
                      project.status === 'active'
                        ? 'badge-green'
                        : project.status === 'completed'
                        ? 'badge-blue'
                        : 'badge-gray'
                    }`}
                  >
                    {project.status}
                  </span>
                </div>

                {project.targetLanguages.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {project.targetLanguages.slice(0, 4).map((lang) => (
                      <span key={lang} className="badge badge-purple text-xs">{lang}</span>
                    ))}
                    {project.targetLanguages.length > 4 && (
                      <span className="badge badge-gray">+{project.targetLanguages.length - 4}</span>
                    )}
                  </div>
                )}

                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Sessions</span>
                    <span>
                      {project.completedSessionCount}
                      {project.targetSessionCount ? ` / ${project.targetSessionCount}` : ''}
                    </span>
                  </div>
                  {pct !== null && (
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-500 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Users size={13} className="text-gray-400" />
                  {project.assignedSupervisorIds && project.assignedSupervisorIds.length > 0 ? (
                    <span>
                      {project.assignedSupervisorIds.length} supervisor
                      {project.assignedSupervisorIds.length === 1 ? '' : 's'} assigned
                    </span>
                  ) : (
                    <span className="text-amber-600">No supervisors — hidden from app</span>
                  )}
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <span className="text-xs text-gray-400">
                    {new Date(project.createdAt).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => openEdit(project)}
                    className="text-gray-400 hover:text-brand-600 p-1 rounded"
                  >
                    <Edit2 size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editProject ? 'Edit Project' : 'Create Project'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Hindi Speech Data Collection"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  className="input h-24 resize-none"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe the project goals..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Languages (comma separated)
                </label>
                <input
                  className="input"
                  value={form.targetLanguages}
                  onChange={(e) => setForm({ ...form, targetLanguages: e.target.value })}
                  placeholder="Hindi, Bhojpuri, Awadhi"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Locations (comma separated)
                </label>
                <input
                  className="input"
                  value={form.targetLocations}
                  onChange={(e) => setForm({ ...form, targetLocations: e.target.value })}
                  placeholder="Uttar Pradesh, Bihar, Jharkhand"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Session Count
                </label>
                <input
                  className="input"
                  type="number"
                  value={form.targetSessionCount}
                  onChange={(e) => setForm({ ...form, targetSessionCount: e.target.value })}
                  placeholder="e.g. 1000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instructions for Enumerators
                </label>
                <textarea
                  className="input h-20 resize-none"
                  value={form.instructions}
                  onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                  placeholder="Special recording instructions..."
                />
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                    checked={form.surveyProject}
                    onChange={(e) => setForm({ ...form, surveyProject: e.target.checked })}
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-700">Survey Project</span>
                    <span className="block text-xs text-gray-500">
                      Uses a stripped recording flow in the app — skips environment photos, preview, and
                      respondent details. Enumerators just capture GPS + language, record the video, and submit.
                    </span>
                  </span>
                </label>
              </div>

              {/* Survey-only options: voice-only capture + custom metadata form */}
              {form.surveyProject && (
                <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                      checked={form.voiceOnly}
                      onChange={(e) => setForm({ ...form, voiceOnly: e.target.checked })}
                    />
                    <span>
                      <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                        <Mic size={14} /> Voice only (audio, no video)
                      </span>
                      <span className="block text-xs text-gray-500">
                        Enumerators record and upload only the respondent's audio — no camera/video.
                      </span>
                    </span>
                  </label>

                  <div className="border-t border-violet-100 pt-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                        checked={form.metadataForm.enabled}
                        onChange={(e) => {
                          const enabled = e.target.checked;
                          setForm({
                            ...form,
                            metadataForm: {
                              enabled,
                              // seed the default fields the first time it's turned on
                              fields:
                                enabled && form.metadataForm.fields.length === 0
                                  ? defaultMetadataFields()
                                  : form.metadataForm.fields,
                            },
                          });
                        }}
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-700">
                          Require metadata form before submit
                        </span>
                        <span className="block text-xs text-gray-500">
                          Uploaders must fill this form before submitting. Defaults to Respondent Language,
                          Age, and Gender — edit freely below.
                        </span>
                      </span>
                    </label>

                    {form.metadataForm.enabled && (
                      <div className="mt-3">
                        <MetadataFormBuilder
                          value={form.metadataForm}
                          onChange={(v) => setForm({ ...form, metadataForm: v })}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Supervisor assignment — controls which teams see this project */}
              <div className="rounded-lg border border-gray-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Users size={15} className="text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Assigned Supervisors</span>
                  <span className="text-xs text-gray-400">
                    ({form.assignedSupervisorIds.length} selected)
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Only these supervisors and their teams (enumerators + transcribers) will see this project
                  in the app. Leave empty to hide it from everyone until you assign someone.
                </p>
                <input
                  className="input mb-2"
                  placeholder="Search supervisors…"
                  value={supervisorFilter}
                  onChange={(e) => setSupervisorFilter(e.target.value)}
                />
                <div className="max-h-40 overflow-y-auto border border-gray-100 rounded-md divide-y divide-gray-50">
                  {supervisors
                    .filter((s) => {
                      const q = supervisorFilter.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        (s.displayName ?? '').toLowerCase().includes(q) ||
                        (s.email ?? '').toLowerCase().includes(q)
                      );
                    })
                    .map((s) => (
                      <label
                        key={s.userId}
                        className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                          checked={form.assignedSupervisorIds.includes(s.userId)}
                          onChange={() => toggleSupervisor(s.userId)}
                        />
                        <span className="text-sm text-gray-700 truncate">
                          {s.displayName || s.email || s.userId}
                        </span>
                      </label>
                    ))}
                  {supervisors.length === 0 && (
                    <div className="px-2 py-2 text-xs text-gray-400">No supervisors found.</div>
                  )}
                </div>
              </div>

              {/* Status — set inactive/completed to remove the project from the app */}
              {editProject && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    className="input"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    <option value="active">Active — visible in app</option>
                    <option value="inactive">Inactive — hidden from app</option>
                    <option value="completed">Completed — hidden from app</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Set to Inactive/Completed to remove this project from all apps when it's done. All
                    collected data is retained.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                className="btn-primary flex-1"
                onClick={handleSubmit}
                disabled={!form.name || !form.description || isPending}
              >
                {isPending ? 'Saving…' : editProject ? 'Save Changes' : 'Create Project'}
              </button>
              <button
                className="btn-secondary flex-1"
                onClick={() => {
                  setShowModal(false);
                  setEditProject(null);
                  setForm(BLANK_FORM);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
